import { JTI_BYTES, MS_PER_SECOND, PROOF_ALG, PROOF_TYP } from '~constants/session';

import { getCurrentKeyPair } from '~libs/sessionKey';

// Milliseconds to add to the local clock to get server time. The API rejects a proof whose iat is more than a
// minute out, so a wrong laptop clock would otherwise lock the user out.
let serverOffsetMs = 0;

/** Tracks the API's clock from an `x-server-time` response header (epoch seconds). */
export const updateServerOffset = (value?: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') return;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  serverOffsetMs = seconds * MS_PER_SECOND - Date.now();
};

export const getServerNowSeconds = () => Math.floor((Date.now() + serverOffsetMs) / MS_PER_SECOND);

// RFC 4648 section 5, no padding. Built with a byte loop: spreading a Uint8Array needs downlevel iteration.
const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const encodeJson = (value: Record<string, unknown>): string =>
  toBase64Url(new TextEncoder().encode(JSON.stringify(value)));

/**
 * Builds the `x-session-proof` compact JWS for one request, or null when this browser has no session key.
 *
 * `htu` is the request path only, exactly as it goes on the wire. The query string is left out because the API
 * compares `htu` with the path alone (RFC 9449 excludes the query too), and the host is left out because
 * proxies may rewrite it. No body hash is signed, so FormData and uploads are untouched.
 */
export const signProof = async (method: string, fullUrl: string): Promise<string | null> => {
  const keyPair = await getCurrentKeyPair();
  if (!keyPair) return null;

  const jti = new Uint8Array(JTI_BYTES);
  crypto.getRandomValues(jti);

  const header = encodeJson({ typ: PROOF_TYP, alg: PROOF_ALG });
  const payload = encodeJson({
    htm: method.toUpperCase(),
    htu: new URL(fullUrl, window.location.href).pathname,
    iat: getServerNowSeconds(),
    jti: toBase64Url(jti),
  });

  const input = `${header}.${payload}`;
  // WebCrypto returns the 64-byte IEEE P1363 r||s form, which is exactly what JWS ES256 carries.
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    keyPair.privateKey,
    new TextEncoder().encode(input),
  );

  return `${input}.${toBase64Url(new Uint8Array(signature))}`;
};
